import morph from "../helpers/morph";
import { initializeTooltips } from "../global/tooltip/tooltip";

window.cartContentRequestId = 1;

// Edits on any line within this window are batched into one /cart/update.js request.
const QUANTITY_DEBOUNCE_MS = 250;

const pendingEdits = new Map();
let pendingTimer = null;
let pendingRequestId = null;

function refreshAllCarts() {
  document.querySelectorAll("cart-content").forEach((cart) => cart._refreshCart?.());
}

function flushPendingEdits() {
  clearTimeout(pendingTimer);
  pendingTimer = null;

  if (!pendingEdits.size) return;

  const updates = Object.fromEntries(pendingEdits);
  const requestId = pendingRequestId;
  const removesOnly = Object.values(updates).every((quantity) => quantity === 0);

  pendingEdits.clear();

  window.updateCart(
    { updates },
    {
      data: { requestId },
      onError: (response) => {
        // A 400 is Shopify's "no valid id or line parameter" — a line has
        // already changed or gone — so it gets the friendly message, and a
        // remove of a line that's already gone needs no message at all.
        const isUnknownLine = response?.httpStatus === 400;
        const message = isUnknownLine
          ? window.localeStrings?.cart?.updateError
          : response?.message || response?.description || window.localeStrings?.cart?.updateError;

        if (message && !(isUnknownLine && removesOnly) && typeof window.showToast === "function") {
          window.showToast(message);
        }

        // Put the screen back to the real cart. Only the latest batch does it;
        // an older one failing is superseded by whatever was queued after it.
        if (requestId === window.cartContentRequestId) {
          refreshAllCarts();
        }
      },
    },
  );
}

function queueQuantityEdit(key, quantity, { immediate = false } = {}) {
  // Claim the newest request id now, not when the batch is sent, so a response
  // still in flight from an earlier batch can't re-render the steppers back to
  // old values while the shopper is still clicking.
  pendingRequestId = ++window.cartContentRequestId;
  pendingEdits.set(key, quantity);

  clearTimeout(pendingTimer);

  if (immediate) {
    flushPendingEdits();
  } else {
    pendingTimer = setTimeout(flushPendingEdits, QUANTITY_DEBOUNCE_MS);
  }
}

function hasUnsavedCartEdits() {
  return pendingEdits.size > 0 || window.isCartBusy?.();
}

// Mirrors an edit onto every cart on the page (the mini-cart and the cart page
// can both be open) before the server answers.
function applyQuantityOnScreen(key, quantity) {
  document.querySelectorAll("cart-content [data-item-key]").forEach((item) => {
    if (item.dataset.itemKey !== key) return;

    if (quantity === 0) {
      item.hidden = true;
      return;
    }

    const input = item.querySelector(".quantity-stepper__input");

    if (input) input.value = quantity;
  });

  document.querySelectorAll("cart-content").forEach((cart) => cart._updateTrackerOnScreen());
}

// Leaving the page (a link outside the cart, closing the tab) with an edit
// still waiting for its debounce: send it anyway so it isn't lost.
window.addEventListener("pagehide", () => {
  if (!pendingEdits.size) return;

  fetch("/cart/update.js", {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ updates: Object.fromEntries(pendingEdits) }),
  });

  pendingEdits.clear();
});

class CartContent extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (e) => {
      const removeButton = e.target.closest("[data-remove]");
      const upsellButton = e.target.closest("[data-add-upsell-to-cart]");
      const link = e.target.closest("a[href]");

      if (removeButton) {
        const item = e.target.closest("[data-item-key]");

        if (!item) return;

        applyQuantityOnScreen(item.dataset.itemKey, 0);
        queueQuantityEdit(item.dataset.itemKey, 0, { immediate: true });

        return;
      }

      if (upsellButton) {
        window.addToCart(
          {
            id: upsellButton.dataset.variantId,
            quantity: 1,
          },
          {
            data: {
              requestId: ++window.cartContentRequestId,
            },
          },
        );

        return;
      }

      // A link out of the cart (View cart, a product, Shop swatches) waits for
      // any unsaved edit, so the next page shows the cart as the shopper left it.
      if (
        link &&
        hasUnsavedCartEdits() &&
        !link.hasAttribute("data-open-drawer") &&
        !link.hasAttribute("data-close-drawer") &&
        !link.target &&
        !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      ) {
        e.preventDefault();
        flushPendingEdits();
        window.whenCartIdle().then(() => {
          window.location.href = link.href;
        });
      }
    });

    // Checkout waits for any unsaved edit too — the cart form doesn't carry
    // quantities, so checking out mid-save would check out the old cart.
    this.addEventListener("submit", (e) => {
      if (!hasUnsavedCartEdits()) return;

      e.preventDefault();

      const form = e.target;
      const button = form.querySelector('[type="submit"]');

      if (button) {
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
      }

      flushPendingEdits();
      window.whenCartIdle().then(() => form.submit());
    });

    document.addEventListener("updateCart", (e) => {
      const requestId = e.detail.data?.requestId;

      if (requestId === window.cartContentRequestId || !requestId) {
        const markup = e.detail.cart.sections?.[this.sectionId];

        if (markup) {
          this._updateCart(markup);
        }
      }
    });

    this.addEventListener("change", (e) => {
      const item = e.target.closest("[data-item-key]");

      if (!item || !e.target.matches(".quantity-stepper__input")) return;

      const quantity = parseInt(e.target.value, 10);

      if (Number.isNaN(quantity)) return;

      applyQuantityOnScreen(item.dataset.itemKey, quantity);
      queueQuantityEdit(item.dataset.itemKey, quantity);
    });
  }

  // The swatch tracker counts the swatch lines still shown, so it moves with
  // the stepper instead of waiting for the save. The re-render that follows
  // the save replaces it with the server's own count.
  _updateTrackerOnScreen() {
    const tracker = this.querySelector("[data-cart-tracker]");

    if (!tracker) return;

    const goal = parseInt(tracker.dataset.goal, 10) || 0;
    let count = 0;

    this.querySelectorAll("[data-item-key][data-swatch-item]").forEach((item) => {
      if (item.hidden) return;

      count += parseInt(item.querySelector(".quantity-stepper__input")?.value, 10) || 0;
    });

    const progress = Math.min(count, goal);
    const countText = tracker.querySelector("[data-cart-tracker-count]");

    if (countText?.dataset.template) {
      countText.textContent = countText.dataset.template.replace("[added]", progress);
    }

    tracker.querySelectorAll(".cart-tracker__segment").forEach((segment, index) => {
      segment.classList.toggle("cart-tracker__segment--filled", index < progress);
    });
  }

  _refreshCart() {
    window
      .fetchPageData({
        path: "/cart",
        options: {
          sectionId: this.sectionId,
        },
      })
      .then((markup) => {
        this._updateCart(markup);
      });
  }

  _updateCart(markup) {
    const html = new DOMParser().parseFromString(markup, "text/html");
    const sourceContent = html.querySelector(`cart-content[cart-section-id="${this.sectionId}"]`);

    if (!sourceContent) return;

    const currentIsEmpty = this.dataset.cartEmpty === "true";
    const nextIsEmpty = sourceContent.dataset.cartEmpty === "true";

    if (currentIsEmpty !== nextIsEmpty) {
      this.replaceWith(sourceContent);

      // Tooltips only initialize on page load, so the fresh markup (e.g. the
      // wedding-details "Why are we asking?" tooltip after the first add to
      // an empty cart) would otherwise never respond to taps.
      initializeTooltips(sourceContent);
      return;
    }

    morph(this, sourceContent);

    // morph can insert tooltip markup or swap its trigger/content nodes, leaving
    // an existing instance bound to detached elements.
    initializeTooltips(this);
  }

  get sectionId() {
    return this.getAttribute("cart-section-id");
  }
}

if (!customElements.get("cart-content")) {
  customElements.define("cart-content", CartContent);
}
